import { ConfigurationFeatureFlag, ConfigurationModel } from '@tink/config/src/tinkLinkConfig';

export type GetConfigurationResponse = {
  configuration: ConfigurationModel;
};

export const shouldSkipUserConfirmation = ({ featureFlags }: ConfigurationModel) => {
  return featureFlags.includes(ConfigurationFeatureFlag.SKIP_USER_IDENTIFICATION);
};

export const hasHiddenTermsAndScopeMessages = ({ featureFlags }: ConfigurationModel) => {
  return (
    featureFlags.includes(ConfigurationFeatureFlag.CREDENTIAL_TERMS_HIDDEN) &&
    featureFlags.includes(ConfigurationFeatureFlag.CREDENTIAL_SCOPES_HIDDEN)
  );
};

export const hasSkipConsentScreen = ({ featureFlags }: ConfigurationModel) => {
  return featureFlags.includes(ConfigurationFeatureFlag.SKIP_CONSENT_SCREEN);
};

export const shouldFallbackToProviderSelection = ({ featureFlags }: ConfigurationModel) => {
  return featureFlags.includes(ConfigurationFeatureFlag.FALLBACK_TO_PROVIDER_SELECTION);
};

export const isHandoffEnabled = ({ featureFlags }: ConfigurationModel) => {
  return !featureFlags.includes(ConfigurationFeatureFlag.DISABLE_HANDOFF);
};

export const hasDemobankScenariosEnabled = ({ featureFlags }: ConfigurationModel) => {
  return featureFlags.includes(ConfigurationFeatureFlag.ENABLE_DEMOBANK_SCENARIOS);
};

export const isBlockListedHandoffEnabled = ({ featureFlags }: ConfigurationModel) => {
  return featureFlags.includes(ConfigurationFeatureFlag.ENABLE_BLOCKLISTED_HANDOFF);
};

export const shouldHideBackButtonWhenProviderSpecified = ({ featureFlags }: ConfigurationModel) => {
  return featureFlags.includes(ConfigurationFeatureFlag.HIDE_BACK_BUTTON_WHEN_PROVIDER_SPECIFIED);
};

export const showLoadingMessages = ({ featureFlags }: ConfigurationModel) => {
  return !featureFlags.includes(ConfigurationFeatureFlag.HIDE_LOADING_MESSAGES);
};

export const shouldAutoRedirectMobileDeviceToThirdParty = ({
  featureFlags,
}: ConfigurationModel) => {
  return featureFlags.includes(
    ConfigurationFeatureFlag.REDIRECT_USER_ON_MOBILE_DEVICE_TO_THIRD_PARTY_AUTOMATICALLY
  );
};

export const shouldRedirectToReferPage = ({ featureFlags }: ConfigurationModel) => {
  return featureFlags.includes(ConfigurationFeatureFlag.REDIRECT_TO_IFRAME_REFER_PAGE);
};

export const userFeedbackEnabled = ({ featureFlags }: ConfigurationModel) => {
  return featureFlags.includes(ConfigurationFeatureFlag.ENABLE_EXIT_FEEDBACK);
};

export const showClientLegalDocs = ({ featureFlags }: ConfigurationModel) => {
  return featureFlags.includes(ConfigurationFeatureFlag.SHOW_CLIENT_LEGAL_DOCS);
};

export const showAppSpecificLegalDocs = ({ featureFlags }: ConfigurationModel) => {
  return featureFlags.includes(ConfigurationFeatureFlag.SHOW_APP_SPECIFIC_LEGAL_DOCS);
};

export const shouldResetCredentialsStatus = ({ featureFlags }: ConfigurationModel) => {
  return featureFlags.includes(ConfigurationFeatureFlag.RESET_TRANSIENT_CREDENTIALS_STATUS);
};

export const isFtsEnabled = ({ featureFlags }: ConfigurationModel, { name }: { name: string }) => {
  // checks both the FF as well as the provider name to trigger FTS flow
  // checking for providerName is a temp solution to support mix of Tink and FTS providers for a client
  // TODO remove the providerName check when long term solution ready on BE side
  return (
    featureFlags.includes(ConfigurationFeatureFlag.ENABLE_PAYMENTS_WITH_FTS) && name.endsWith('-sp')
  );
};

export const isAccountCheckBundled = ({ featureFlags }: ConfigurationModel) => {
  return featureFlags.includes(ConfigurationFeatureFlag.PAYMENT_ACCOUNT_CHECK_BUNDLE);
};

export const showSuccessScreenForAccountCheck = ({ featureFlags }: ConfigurationModel) => {
  return featureFlags.includes(ConfigurationFeatureFlag.ENABLE_SUCCESS_SCREEN_AC);
};

export const showSuccessScreenForIncomeCheck = ({ featureFlags }: ConfigurationModel) => {
  return featureFlags.includes(ConfigurationFeatureFlag.ENABLE_SUCCESS_SCREEN_IC);
};

export const showSuccessScreenForRiskInsights = ({ featureFlags }: ConfigurationModel) => {
  return featureFlags.includes(ConfigurationFeatureFlag.ENABLE_SUCCESS_SCREEN_RI);
};

export const isMerchantNetflix = ({ featureFlags }: ConfigurationModel) => {
  return featureFlags.includes(ConfigurationFeatureFlag.MERCHANT_NETFLIX);
};

export const isConsentFlow = ({ featureFlags }: ConfigurationModel) => {
  return featureFlags.includes(ConfigurationFeatureFlag.OPT_IN_CONSENT_FLOW);
};

export const isMerchantNetflixConsentFlow = ({ featureFlags }: ConfigurationModel) => {
  return (
    featureFlags.includes(ConfigurationFeatureFlag.OPT_IN_CONSENT_FLOW) &&
    featureFlags.includes(ConfigurationFeatureFlag.MERCHANT_OPT_IN_NETFLIX_CONSENT)
  );
};

export const isMultibanking = ({ featureFlags }: ConfigurationModel) => {
  return featureFlags.includes(ConfigurationFeatureFlag.MULTIBANKING);
};

export const navigateUserToInitialTinkLinkAfterThirdPartyRedirect = ({
  featureFlags,
}: ConfigurationModel) => {
  return featureFlags.includes(
    ConfigurationFeatureFlag.NAVIGATE_USER_TO_INITIAL_TINK_LINK_AFTER_3RD_PARTY_REDIRECT
  );
};

export const haveFeatureFlag = (
  { featureFlags }: ConfigurationModel,
  flag: ConfigurationFeatureFlag
) => {
  return featureFlags.includes(flag);
};
